/**
 * Authenticated fetch wrapper for Zephyr frontend services.
 * Automatically adds Bearer token from AuthService and handles token refresh.
 * 
 * Usage:
 *   import { authFetch } from './authFetch.js';
 *   const response = await authFetch(`${API_URL}/api/profile`, { method: 'PUT', body: ... });
 * 
 * SECURITY:
 * - Always use this for authenticated endpoints (profile, account, subscription, etc.)
 * - Use regular fetch() for public endpoints (wind, elevation, autocomplete, etc.)
 * - Never pass userId in query params or body — backend extracts from token
 */

/**
 * Fetch wrapper that adds Authorization: Bearer header.
 * Refreshes token if needed before making the request.
 * Redirects to sign-in if token is unavailable.
 * 
 * @param {string} url - Request URL
 * @param {Object} options - Standard fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - The fetch Response object
 */
export async function authFetch(url, options = {}) {
    // Get the global authService instance
    const authService = window.authService;
    if (!authService) {
        console.error('authFetch: AuthService not initialized');
        throw new Error('Authentication not available');
    }

    // Ensure token is fresh (refreshes silently if within 5min of expiry)
    const isValid = await authService.refreshIfNeeded();
    if (!isValid) {
        // Token refresh failed — session expired, redirect to sign-in
        console.warn('authFetch: Token refresh failed, signing out');
        authService.signOut();
        throw new Error('Session expired — please sign in again');
    }

    const token = authService.getAccessToken();
    if (!token) {
        console.warn('authFetch: No access token available');
        authService.signOut();
        throw new Error('Session expired — please sign in again');
    }

    // Merge Authorization header with any existing headers
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
        'Authorization': `Bearer ${token}`
    };

    return fetch(url, { ...options, headers });
}
